import { memo,  useEffect, useRef, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import {useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { v4 } from "uuid";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useAutoSave } from "./hooks/useAutoSave";
import FormSection from "./components/features/FormSection";
import { deleteSection, sectionAutoSave, setFormData } from "./slices/FbSections";
import { fieldTypeDefaults } from "./utils/schemas/initial";
import { notify } from "../../bmr/utils/methods/notification";
import DeleteModal from "../../bmr/components/common/BaseModal/DeleteModal";
import { ConfirmationInfoIcon } from "../../assets/common-svg/CommonSvg";
import { incrementVersion } from "../../bmr/slices/formTemplate";

const FormContent = ({
  SectionSchema,
  selectedSection,
  selectedField,
  setSelectedSection,
  setSelectedField,
  setIsSectionAdd,
  isSectionAdd,
  setIsFieldAdd,
  isFieldAdd
}) => {
  const { control, setValue, getValues } = useFormContext();
  const {
    fields: sections,
    append,
    move: sectionMove,
    remove,
  } = useFieldArray({ control, name: "sections" });
  const sectionSchema = useWatch({ control, name: "sections" });
  const dispatch = useDispatch();
  const navigate=useNavigate();
  const { id } = useParams();

  const  [openVersionIncreaseModal,setOpenVersionIncreaseModal]=useState(false);

    const templateDetails = useSelector((state) => state?.bmrFbl?.fbTemplate?.items || []);
  const currentFormTemplate = templateDetails.find(template => template?.id === +id);
  // console.log('curr',currentFormTemplate.id);
  const addNewSection = async () => {
    try{
        if (currentFormTemplate?.status === 2) {
      setOpenVersionIncreaseModal(true);   
      return;                  
    }
      setIsSectionAdd(true)
    append({
      ...SectionSchema,
      section_uuid: v4(),
      order: sections.length + 1,
      fields: [
        {
          ...SectionSchema?.fields[0],
          input_uuid: v4(),
          order: 1,
        },
      ],
    });
    const updatedSections = getValues("sections");
    const clonedSections = JSON.parse(JSON.stringify(updatedSections));
    dispatch(setFormData({ sections: clonedSections }));
    const response = await dispatch(sectionAutoSave({
      id: id,
      data: { sections: clonedSections }
    })).unwrap();
    if (response) {
      notify.success("Section added successfully")
    }}catch{
      setIsSectionAdd(false)
    }finally{
      setIsSectionAdd(false)
    }

  };

  //@info adding this to remove section.
  const removeSection = async ({ sectionId, index }) => {
    if (sections?.length === 1) {
      console.warn("Cannot remove the last section.");
      return;
    }
    try {
      remove(index);
      const updatedSections = getValues("sections");
      const clonedSections = JSON.parse(JSON.stringify(updatedSections));
      dispatch(setFormData({ sections: clonedSections }));

      if (id && sectionId) {
       const resp=await dispatch(deleteSection({ id, sectionId }));
       if(resp.payload.success){
        notify.success(resp.payload.message)
       }
      } else {
        console.warn("Missing `id` or `sectionId` for deleteSection dispatch.");
      }
    } catch (error) {
      console.error("Error removing section:", error);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const handleSectionDragEnd = (event) => {
  const { active, over } = event;
  if (!over || active.id === over.id) return;

  const oldIndex = sections.findIndex((s) => s.section_uuid === active.id);
  const newIndex = sections.findIndex((s) => s.section_uuid === over.id);

  sectionMove(oldIndex, newIndex);

  const updatedSections = getValues("sections").map((section, idx) => ({
    ...section,
    order: idx + 1,
  }));
  setValue("sections", updatedSections);
};

 useAutoSave({
    saveFunction: async (data) => {
      return await dispatch(sectionAutoSave({ data, id }));
    },
  });

 const handleSectionDragStart = async () => {
    const currentFormData = getValues();
    await dispatch(sectionAutoSave({ data: currentFormData, id }));
  };
  const prevFieldTypeRef = useRef();

  useEffect(() => {
    const sectionIndex = sectionSchema.findIndex(
      (section) => section?.section_uuid === selectedSection
    );

    const fieldIndex =
      sectionIndex !== -1
        ? sectionSchema[sectionIndex]?.fields?.findIndex(
            (field) => field?.input_uuid === selectedField
          )
        : -1;

    const field_type =
      sectionSchema?.[sectionIndex]?.fields?.[fieldIndex]?.property?.field_type;

    const field_layout =
      sectionSchema?.[sectionIndex]?.fields?.[fieldIndex]?.property
        ?.field_layout;

    if (
      field_type &&
      fieldTypeDefaults[field_type] &&
      prevFieldTypeRef.current !== field_type
    ) {
      setValue(`sections.${sectionIndex}.fields.${fieldIndex}.property`, {
        field_layout,
        field_type,
        ...fieldTypeDefaults[field_type],
      });
      prevFieldTypeRef.current = field_type;

    }
  }, [selectedSection, selectedField, sectionSchema]);

  const handleAccept=async ()=>{
    try {
      const response = await dispatch(incrementVersion(id)).unwrap();
      const newVersionId = response?.data?.newTemplate?.id;
  setOpenVersionIncreaseModal(false);
      navigate(`/bmr/form-management/${newVersionId}`);
}
 catch (error) {
      console.error("Version increment failed:", error);
 }
}
const handleRejection = async () => {
  setOpenVersionIncreaseModal(false)
}

  return (
    <>
     {/* @info to show auto save status */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleSectionDragEnd}
        onDragStart={handleSectionDragStart}
      >
        <SortableContext
          items={sections.map((s) => s.section_uuid)}
          strategy={verticalListSortingStrategy}
        >
          {sections.map((section, index) => (
            <FormSection
              key={section?.section_uuid}
              index={index}
              section={section}
              selectedSection={selectedSection}
              setSelectedSection={setSelectedSection}
              selectedField={selectedField}
              setSelectedField={setSelectedField}
              addNewSection={addNewSection}
              removeSection={removeSection}
              noOfSections={sections?.length}
              isSectionAdd={isSectionAdd}
              isFieldAdd={isFieldAdd}
              setIsFieldAdd={setIsFieldAdd}
            />
          ))}
          <DeleteModal
            open={openVersionIncreaseModal}
            setClose={setOpenVersionIncreaseModal}
            title={
              <>
                <span>You have already published this form.</span>
                <br />
                <span>If you want to add new sections, you will need to create a new version of the form.</span>
                <br />
                <span>Do you want to proceed?</span>
              </>
            }
            buttonText="Yes"
            fontWeight={300}
            btnPrimary={"#90fb2cff"}
            handleSuccess={handleAccept}
            handleRejection={handleRejection}
            rejectButtonText="No"
            otherIcon={<ConfirmationInfoIcon Height={110} Width={110} Filled="#FB9D2C" />}
          />
        </SortableContext>
      </DndContext>
    </>
  );
};

export default memo(FormContent);